import { blockAssets, type Block, type DesignSystemDto } from '@ads/shared';
import { renderSite, splitPages, type SitePage } from '@ads/site';
import { mediaUrl } from '../doc/Doc';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as Ic from '../components/icons';

/* ─────────────────────────────────────────────────────────────────────
   O canvas: o site de verdade, desenhado pelo mesmo renderizador que
   gera o pacote publicado. Um iframe sem scripts (sandbox) recebe o
   HTML; a cada bloco novo só as faixas que mudaram são trocadas, então
   nada pisca e a animação acontece só no que acabou de chegar.
   ───────────────────────────────────────────────────────────────────── */

const DEVICES = [
  { key: 'desktop', w: 1280, label: 'Computador', Icon: Ic.Desktop },
  { key: 'tablet', w: 834, label: 'Tablet', Icon: Ic.Tablet },
  { key: 'phone', w: 390, label: 'Celular', Icon: Ic.Phone },
] as const;
type Device = (typeof DEVICES)[number]['key'];

const CHROME = 34;

export interface SiteCanvasProps {
  title: string;
  orgName: string;
  blocks: Block[];
  ds: DesignSystemDto | null | undefined;
  primaryLink?: string;
  /** blocos a partir deste índice entram animados */
  liveFrom?: number;
  /** blocos que acabaram de mudar na conversa: animam, e o canvas vai até o mais recente */
  fresh?: readonly number[];
  running?: boolean;
  /** terminou de escrever: volta ao topo (geração) ou fica onde a mudança aconteceu (conversa) */
  settleTop?: boolean;
  /** domínio que aparece na barra do navegador */
  host?: string | null;
  /** página controlada de fora (ex.: estrutura na coluna da direita) */
  page?: string | null;
  onPage?: (slug: string) => void;
  /** link para abrir a prévia numa aba */
  openHref?: string | null;
}

/** Troca só os filhos que mudaram, comparando a chave data-k de cada faixa. */
function patch(doc: Document, old: Element, next: ParentNode) {
  const byKey = new Map<string, Element>();
  for (const el of Array.from(old.children)) { const k = el.getAttribute('data-k'); if (k) byKey.set(k, el); }
  let cursor: Element | null = old.firstElementChild;
  for (const n of Array.from(next.children)) {
    const k = n.getAttribute('data-k');
    let use: Element;
    if (k && byKey.has(k)) { use = byKey.get(k)!; byKey.delete(k); }
    else if (!k && cursor && !cursor.getAttribute('data-k') && cursor.tagName === n.tagName && cursor.id === n.id) { patch(doc, cursor, n); use = cursor; }
    else use = doc.importNode(n, true);
    if (use === cursor) cursor = cursor.nextElementSibling;
    else old.insertBefore(use, cursor);
  }
  while (cursor) { const nx: Element | null = cursor.nextElementSibling; cursor.remove(); cursor = nx; }
}

export function SiteCanvas(p: SiteCanvasProps) {
  const { blocks, ds, running = false, fresh, settleTop = true } = p;
  /* fotos da conversa: a prévia busca pela API, na mesma origem */
  const media = useMemo(() => Object.fromEntries(blocks.flatMap(blockAssets).map(id => [id, { src: mediaUrl(id) }])), [blocks]);
  const out = useMemo(() => renderSite({
    title: p.title, blocks, org: { name: p.orgName }, mode: 'preview', primaryLink: p.primaryLink, newFrom: p.liveFrom, fresh, media,
    ds: ds ? { hash: ds.hash, css: ds.css, logo: ds.logo, themeColor: ds.themeColor } : null,
  }), [p.title, blocks, p.orgName, p.primaryLink, p.liveFrom, fresh, media, ds]);

  /* página: a escolhida, ou a mais nova enquanto o agente escreve */
  const [picked, setPicked] = useState<string | null>(null);
  /* a conversa mudou um bloco de outra página: o canvas vai até ela */
  const last = fresh?.length ? fresh[fresh.length - 1]! : null;
  const focusSlug = useMemo(() => (last === null ? null : splitPages(blocks).find(pg => pg.items.some(it => it.i === last))?.slug ?? null), [blocks, last]);
  useEffect(() => { if (focusSlug !== null) setPicked(focusSlug); }, [focusSlug]);
  const want = p.page !== undefined && p.page !== null ? p.page : picked;
  const page: SitePage = out.pages.find(pg => pg.slug === want) ?? (running ? out.pages[out.pages.length - 1]! : out.pages[0]!);
  const choose = useCallback((slug: string) => { setPicked(slug); p.onPage?.(slug); }, [p.onPage]);

  /* no celular, começa no celular: 1280px encolhidos para 350 não se leem */
  const [device, setDevice] = useState<Device>(() => (typeof window !== 'undefined' && window.innerWidth < 720 ? 'phone' : 'desktop'));
  const dw = DEVICES.find(d => d.key === device)!.w;

  /* cabe na caixa: a página tem a largura do aparelho e encolhe sem refluir */
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const padX = size.w < 640 ? 12 : 28;
  const scale = size.w ? Math.min(1, (size.w - padX * 2) / dw) : .5;
  const visH = Math.max(240, size.h - 24 - CHROME);
  const frameH = visH / scale;

  /* o iframe: recarrega só quando o <head> muda; o corpo é remendado */
  const frame = useRef<HTMLIFrameElement>(null);
  const pageRef = useRef(page); pageRef.current = page;
  const runRef = useRef(running); runRef.current = running;
  const st = useRef({ head: '', ready: false, body: '' });
  const follow = useRef(true);
  const [following, setFollowing] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const sync = useCallback(() => {
    const f = frame.current, doc = f?.contentDocument, s = st.current, pg = pageRef.current;
    if (!doc?.body || !s.ready) return;
    if (s.body !== pg.body) {
      const tpl = doc.createElement('template');
      tpl.innerHTML = pg.body;
      patch(doc, doc.body, tpl.content);
      s.body = pg.body;
    }
    /* acompanha o que acabou de chegar, a menos que a pessoa esteja lendo */
    if (runRef.current && follow.current) {
      const all = doc.querySelectorAll('[data-new]');
      const el = all[all.length - 1];
      const win = doc.defaultView;
      if (el && win) {
        const top = el.getBoundingClientRect().top + win.scrollY - 72;
        win.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }
  }, []);

  useEffect(() => {
    const f = frame.current, s = st.current;
    if (!f) return;
    const head = page.htmlAttrs + page.head;
    if (head !== s.head) {
      s.head = head; s.ready = false; s.body = page.body;
      f.srcdoc = page.html;
      return;
    }
    sync();
  }, [page, sync]);

  const onLoad = () => {
    const doc = frame.current?.contentDocument;
    if (!doc) return;
    st.current.ready = true;
    doc.addEventListener('click', e => {
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a) return;
      e.preventDefault();
      const pg = a.getAttribute('data-page');
      const href = a.getAttribute('href') ?? '';
      if (pg !== null) { choose(pg); return; }
      if (href.startsWith('#')) { doc.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      setToast(href);
    });
    const stop = () => { if (runRef.current && follow.current) { follow.current = false; setFollowing(false); } };
    doc.addEventListener('wheel', stop, { passive: true });
    doc.addEventListener('touchmove', stop, { passive: true });
    if (st.current.body !== pageRef.current.body) sync();
    else if (runRef.current) sync();
  };

  /* começou a escrever: acompanha. Terminou: volta ao topo, para a aprovação começar pela primeira dobra —
     ou, na conversa, fica na última mudança */
  const wasRunning = useRef(running);
  useEffect(() => {
    if (running) { follow.current = true; setFollowing(true); }
    else if (wasRunning.current && settleTop) {
      const t = setTimeout(() => frame.current?.contentWindow?.scrollTo({ top: 0, behavior: 'smooth' }), 900);
      wasRunning.current = running;
      return () => clearTimeout(t);
    } else if (wasRunning.current && follow.current) {
      const t = setTimeout(() => {
        const doc = frame.current?.contentDocument, win = doc?.defaultView;
        const all = doc?.querySelectorAll('[data-new]');
        const el = all?.[all.length - 1];
        if (el && win) win.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + win.scrollY - 72), behavior: 'smooth' });
      }, 320);
      wasRunning.current = running;
      return () => clearTimeout(t);
    }
    wasRunning.current = running;
  }, [running, settleTop]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5200);
    return () => clearTimeout(t);
  }, [toast]);

  const empty = blocks.filter(b => b.type !== 'page').length === 0;
  const host = p.host ?? 'prévia';

  return (
    <div className="sc">
      <div className="sc-bar">
        <div className="sc-pages" role="tablist" aria-label="Páginas">
          {out.pages.length > 1 ? out.pages.map(pg => (
            <button key={pg.slug} type="button" role="tab" aria-selected={pg.slug === page.slug} onClick={() => choose(pg.slug)}>{pg.title}</button>
          )) : <span className="sc-one">{empty ? 'Página' : 'Página única'}</span>}
        </div>
        {running && !following && (
          <button className="sc-follow" type="button" onClick={() => { follow.current = true; setFollowing(true); sync(); }}>
            <span className="sc-pulse" />Acompanhar a escrita
          </button>
        )}
        <div className="sc-dev" role="group" aria-label="Tamanho da tela">
          {DEVICES.map(d => (
            <button key={d.key} type="button" aria-pressed={device === d.key} title={`${d.label} · ${d.w}px`} onClick={() => setDevice(d.key)}>
              <d.Icon /><span className="sr">{d.label}</span>
            </button>
          ))}
        </div>
        {p.openHref && !running && (
          <a className="sc-open" href={p.openHref} target="_blank" rel="noopener"><Ic.External />Abrir em nova aba</a>
        )}
      </div>

      <div className="sc-box" ref={box}>
        <div className="sc-device" style={{ width: Math.round(dw * scale), height: visH + CHROME }} data-device={device}>
          <div className="sc-chrome" aria-hidden="true">
            <span className="sc-dots"><i /><i /><i /></span>
            <span className="sc-url"><Ic.ShieldCheck />{host}{page.href === '/' ? '' : page.href.replace(/\/$/, '')}</span>
          </div>
          <div className="sc-frame" style={{ height: visH }}>
            <iframe ref={frame} title="Pré-visualização do site" sandbox="allow-same-origin" onLoad={onLoad}
              style={{ width: dw, height: frameH, transform: `scale(${scale})` }} />
            {empty && (
              <div className="sc-wait" aria-live="polite">
                <div className="sc-wait-hero"><i /><i /><i /><b /></div>
                <div className="sc-wait-row"><i /><i /><i /></div>
                <p>{running ? 'Desenhando a primeira dobra…' : 'Nenhum conteúdo ainda.'}</p>
              </div>
            )}
          </div>
        </div>
        {toast && (
          <div className="sc-toast" role="status">
            <span>No site publicado, este botão leva para</span>
            <b>{toast.replace(/^mailto:|^tel:|^https?:\/\//, '')}</b>
            {/^https?:\/\//.test(toast) && <a href={toast} target="_blank" rel="noopener">Testar<Ic.External /></a>}
          </div>
        )}
      </div>
    </div>
  );
}
